import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import TimePortal from './components/TimePortal';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import Dashboard from './components/Dashboard';
import Units from './components/Units';
import Team from './components/Team';
import Registration from './components/Registration';
import WelcomeScreen from './components/WelcomeScreen';
import Toast from './components/Toast';
import Loader from './components/Loader';
import TelegramModal from './components/TelegramModal';
import ActivitiesModal from './components/ActivitiesModal';
import Tour from './components/Tour';
import { useWallet } from './hooks/useWallet';
import { useUserData } from './hooks/useUserData';

// Tab order for swipe navigation
const TABS = ['dashboard', 'units', 'team'];

function App() {
  const { account, balance, usdtBalance, isConnecting, isConnected, connect, disconnect, refreshBalances } = useWallet();
  const { 
    userId, userData, packages, incomeBreakdown, claimableIncome, 
    referrals, activities, poolEligibility, royaltyEligible,
    isLoading, isRegistered, refreshData 
  } = useUserData(account);
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [slideDirection, setSlideDirection] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [showTelegram, setShowTelegram] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Swipe detection refs
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndX = useRef(null);

  // Handle referral from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) localStorage.setItem('referrerId', ref);
  }, []);

  // Check if tour should be shown
  useEffect(() => {
    if (isRegistered && !localStorage.getItem('timetotime_tour_completed')) {
      const timer = setTimeout(() => setShowTour(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isRegistered]);

  // Scroll progress for portal effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Tab change with animation
  const changeTab = (newTab, direction) => {
    if (newTab === activeTab || isAnimating) return;

    // Auto-detect direction if not provided
    if (!direction) {
      const currentIndex = TABS.indexOf(activeTab);
      const newIndex = TABS.indexOf(newTab);
      direction = newIndex > currentIndex ? 'left' : 'right';
    }

    setIsAnimating(true);
    setSlideDirection(`slide-out-${direction}`);

    setTimeout(() => {
      setActiveTab(newTab);
      setSlideDirection(`slide-in-${direction}`);
      
      // Scroll to top on tab change
      window.scrollTo({ top: 0, behavior: 'smooth' });

      setTimeout(() => {
        setSlideDirection('');
        setIsAnimating(false);
      }, 300);
    }, 200);
  };

  // Swipe handlers
  const handleTouchStart = (e) => {
    if (showTelegram || showActivities || showTour) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    if (showTelegram || showActivities || showTour) return;

    const deltaX = touchStartX.current - touchEndX.current;
    const deltaY = Math.abs(touchStartY.current - (touchEndX.current || 0));
    const minSwipeDistance = 50;

    const startX = touchStartX.current;
    const screenWidth = window.innerWidth;
    const edgeThreshold = 30;

    if (startX < edgeThreshold || startX > screenWidth - edgeThreshold) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }

    if (Math.abs(deltaX) > minSwipeDistance && Math.abs(deltaX) > deltaY) {
      const currentIndex = TABS.indexOf(activeTab);

      if (deltaX > 0) {
        // Swiped left - go to next tab
        if (currentIndex < TABS.length - 1) {
          changeTab(TABS[currentIndex + 1], 'left');
        }
      } else {
        // Swiped right - go to previous tab
        if (currentIndex > 0) {
          changeTab(TABS[currentIndex - 1], 'right');
        }
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
    touchStartY.current = null;
  };

  // Handle bottom nav tab change with animation
  const handleTabChange = (newTab) => {
    changeTab(newTab);
  };

  const addToast = (type, message) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const handleConnect = async () => {
    try {
      await connect();
      addToast('success', 'Wallet connected successfully!');
    } catch (error) {
      addToast('error', error.message || 'Failed to connect wallet');
    }
  };

  const handleRefresh = () => {
    refreshData();
    refreshBalances();
    addToast('success', 'Data refreshed');
  };

  const renderContent = () => {
    if (!isConnected) {
      return <WelcomeScreen onConnect={handleConnect} isConnecting={isConnecting} />;
    }

    if (isLoading) {
      return <Loader message="Traveling through time..." />;
    }

    if (!isRegistered) {
      return <Registration onSuccess={handleRefresh} addToast={addToast} />;
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <Dashboard
            userId={userId}
            userData={userData}
            packages={packages}
            incomeBreakdown={incomeBreakdown}
            claimableIncome={claimableIncome}
            referrals={referrals}
            activities={activities}
            poolEligibility={poolEligibility}
            royaltyEligible={royaltyEligible}
            usdtBalance={usdtBalance}
            onRefresh={handleRefresh}
            addToast={addToast}
            onShowActivities={() => setShowActivities(true)}
            onShowTelegram={() => setShowTelegram(true)}
          />
        );
      case 'units':
        return (
          <Units
            packages={packages}
            userId={userId}
            onSuccess={handleRefresh}
            addToast={addToast}
          />
        );
      case 'team':
        return (
          <Team
            userId={userId}
            userData={userData}
            packages={packages}
            referrals={referrals}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className="app" 
      style={{ '--scroll-progress': scrollProgress }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <TimePortal scrollProgress={scrollProgress} />
      
      {isConnected && isRegistered && (
        <Header 
          account={account}
          usdtBalance={usdtBalance}
          onDisconnect={disconnect}
          onShowTelegram={() => setShowTelegram(true)}
        />
      )}

      <main className={`main-content ${slideDirection}`}>
        {renderContent()}
      </main>

      {isConnected && isRegistered && (
        <BottomNav activeTab={activeTab} setActiveTab={handleTabChange} />
      )}

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} type={toast.type} message={toast.message} />
        ))}
      </div>

      {/* Modals */}
      {showTelegram && (
        <TelegramModal
          userId={userId}
          userData={userData}
          incomeBreakdown={incomeBreakdown}
          onClose={() => setShowTelegram(false)}
          addToast={addToast}
        />
      )}

      {showActivities && (
        <ActivitiesModal
          activities={activities}
          onClose={() => setShowActivities(false)}
        />
      )}

      {/* Tour for first-time users */}
      {showTour && (
        <Tour onComplete={() => setShowTour(false)} />
      )}
    </div>
  );
}

export default App;